import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import SharedProfile from '../../common/Profile/SharedProfile';
import {
  fetchExternalUserById,
  updateExternalUser,
  fetchExternalRoles,
  fetchCategories,
} from './externalUsersService';
import {
  checkPrivilegeOnResource,
  getCurrentUser,
} from '../../../services/authService';
import { RESOURCES } from '../../../constants/resourceConstants';
import { PRIVILEGES } from '../../../constants/privilege.constant';
import { ROLE_STATUS } from '../../../constants/role.constant';
import useUserPrivilegesApi from '../../../hooks/api/users/useUserPrivilegesApi';
import { formatPhoneForStorage } from '../../../utils/phoneUtils';

export default function ExternalUserProfileEdit({ persona }) {
  const [t] = useTranslation();
  const { id } = useParams();
  const [userData, setUserData] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [hasAccess, setHasAccess] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [roles, setRoles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [targetPrivileges, setTargetPrivileges] = useState(null);
  const [targetHasCustomPriv, setTargetHasCustomPriv] = useState(false);

  const {
    updateUserPrivileges,
    getGrantedPrivileges,
    hasCustomPrivileges: checkHasCustomPrivileges,
  } = useUserPrivilegesApi();

  useEffect(() => {
    const loadData = async () => {
      try {
        if (!id) return;

        const results = await Promise.allSettled([
          fetchExternalUserById(id),
          fetchExternalRoles(),
          getGrantedPrivileges(id),
          checkHasCustomPrivileges(id),
          fetchCategories(),
        ]);

        const [
          userResult,
          rolesResult,
          privilegesResult,
          customPrivResult,
          categoriesResult,
        ] = results;

        if (userResult.status !== 'fulfilled') throw userResult.reason;
        if (rolesResult.status !== 'fulfilled') throw rolesResult.reason;
        if (privilegesResult.status !== 'fulfilled')
          throw privilegesResult.reason;

        if (customPrivResult.status !== 'fulfilled')
          throw customPrivResult.reason;

        const categoriesData =
          categoriesResult.status === 'fulfilled' ? categoriesResult.value : [];

        if (categoriesResult.status === 'rejected') {
          console.error('Error loading categories:', categoriesResult.reason);
        }

        const catItems = Array.isArray(categoriesData)
          ? categoriesData
          : categoriesData?.data || [];

        setUserData(userResult.value);
        setRoles(
          rolesResult.value.filter(
            (role) => role.status === ROLE_STATUS.PUBLISHED_ACTIVE
          )
        );
        setCategories(catItems);
        setTargetPrivileges(privilegesResult.value);
        setTargetHasCustomPriv(customPrivResult.value);
        setLoading(false);
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Error loading user data');
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  useEffect(() => {
    const checkUserPrivileges = async () => {
      try {
        const currentUserData = await getCurrentUser();
        const userIsSuperAdmin = currentUserData?.user?.is_super === 1;
        setIsSuperAdmin(userIsSuperAdmin);
        if (userIsSuperAdmin) {
          setHasAccess(true);
          return;
        }
        const access = await checkPrivilegeOnResource(
          PRIVILEGES.EDIT,
          RESOURCES.EXTERNAL_USERS
        );
        setHasAccess(access);
      } catch (error) {
        console.error('Error checking privileges:', error);
        setHasAccess(false);
      }
    };

    checkUserPrivileges();
  }, [persona]);

  const validateFormData = useCallback(
    (formData, section) => {
      const errors = {};

      const checkUsername =
        section === 'personal' || section === 'contact' || section === 'full';

      if (checkUsername) {
        if (!formData.username || formData.username.trim() === '') {
          errors.username = t(
            'validation.usernameRequired',
            'Username is required'
          );
        } else if (formData.username.trim().length < 3) {
          errors.username = t(
            'validation.usernameMinLength',
            'Username must be at least 3 characters long'
          );
        }
      }

      if (section === 'contact' || section === 'full') {
        if (!formData.email || formData.email.trim() === '') {
          errors.email = t('validation.emailRequired', 'Email is required');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          errors.email = t(
            'validation.emailInvalid',
            'Please enter a valid email address'
          );
        }

        if (formData.phone && formData.phone.trim() !== '') {
          const hasDigits = /\d/.test(formData.phone);
          const isReasonableLength =
            formData.phone.length >= 8 && formData.phone.length <= 20;
          if (!hasDigits || !isReasonableLength) {
            errors.phone = t(
              'validation.phoneInvalid',
              'Please enter a valid phone number'
            );
          }
        }
      }

      return errors;
    },
    [t]
  );

  const clearFieldError = useCallback((fieldName) => {
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  }, []);

  const handleSave = useCallback(
    async (formData, section) => {
      setValidationErrors({});

      const errors = validateFormData(formData, section);
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        const firstError = Object.values(errors)[0];
        toast.error(firstError);
        throw new Error('VALIDATION_ERROR');
      }

      try {
        const updatePayload = {
          username: formData.username || '',
          email: formData.email || '',
          phone: formData.phone ? formatPhoneForStorage(formData.phone) : null,
          category: formData.category || '',
          role: formData.role || '',
        };

        await updateExternalUser(id, updatePayload);

        const updatedUser = await fetchExternalUserById(id);
        setUserData(updatedUser);
      } catch (error) {
        if (
          error.response?.status === 400 &&
          error.response?.data?.message === 'Email already exists'
        ) {
          setValidationErrors({
            email: t(
              'validation.emailExists',
              'This email is already in use by another user'
            ),
          });
          toast.error(
            t(
              'validation.emailExists',
              'This email is already in use by another user'
            )
          );
          throw new Error('VALIDATION_ERROR');
        }
        throw error;
      }
    },
    [id, validateFormData, t]
  );

  const handleSaveRoleAndPrivileges = useCallback(
    async ({
      roleId,
      privileges,
      roleChanged,
      privilegesChanged,
      categoryId,
      categoryChanged,
    }) => {
      try {
        const updatePayload = {};
        if (roleChanged) {
          updatePayload.role = roleId;
        }
        if (categoryChanged) {
          updatePayload.category = categoryId;
        }
        if (Object.keys(updatePayload).length > 0) {
          await updateExternalUser(id, updatePayload);
        }

        if (privilegesChanged) {
          await updateUserPrivileges(id, privileges);
        }

        toast.success(t('privilegesSection.saveSuccess'));

        const updatedUser = await fetchExternalUserById(id);
        setUserData(updatedUser);
      } catch (e) {
        const errorMsg =
          e.response?.data?.message || t('privilegesSection.saveError');
        toast.error(errorMsg);
        throw e;
      }
    },
    [id, updateUserPrivileges, t]
  );

  const personalFields = [
    {
      name: 'username',
      type: 'text',
      label: t('Username'),
      required: true,
      fullWidth: true,
    },
  ];

  if (loading) return <div>Loading...</div>;
  if (!userData) return <div>User not found</div>;

  return (
    <SharedProfile
      userData={userData}
      canEditProfile={hasAccess}
      showPasswordTab={false}
      showRolesTab={true}
      allowEmailEdit={isSuperAdmin}
      onSave={hasAccess ? handleSave : undefined}
      personalFields={personalFields}
      validationErrors={validationErrors}
      clearFieldError={clearFieldError}
      showPrivilegesEditor={hasAccess}
      roleOptions={roles}
      categoryOptions={categories}
      onSaveRoleAndPrivileges={handleSaveRoleAndPrivileges}
      isInternal={false}
      privilegesUserId={id}
      userPrivileges={targetPrivileges}
      hasCustomPrivileges={targetHasCustomPriv}
    />
  );
}
